/**
 * Unified HTTP API Client
 * Secure, optimized, and extensible HTTP client with authentication support
 */

import { ApiConfig, HttpMethod, RequestBody, RequestOptions, ApiResponse, IHttpClient, RequestConfig, RequestInterceptor, ResponseInterceptor, ErrorInterceptor } from './types';
import { defaultApiConfig, ApiErrorCode } from './config';

// Enhanced API Error class
export class ApiError extends Error implements ApiError {
    constructor(
        public readonly status: number,
        public readonly statusText: string,
        public readonly code: ApiErrorCode,
        message?: string,
        public readonly response?: Response,
        public readonly requestId?: string,
        public readonly errorCode?: string // Backend error code (e.g., "no_active_subscription")
    ) {
        super(message || `API Error: ${status} ${statusText}`);
        this.name = 'ApiError';
        Object.setPrototypeOf(this, ApiError.prototype);
    }

    isRetryable(): boolean {
        return (
            this.status === 408 ||
            this.status === 425 ||
            this.status === 429 ||
            this.status >= 500 ||
            this.code === ApiErrorCode.NETWORK_ERROR ||
            this.code === ApiErrorCode.TIMEOUT_ERROR
        );
    }

    isClientError(): boolean {
        return this.status >= 400 && this.status < 500;
    }

    isServerError(): boolean {
        return this.status >= 500;
    }
}

// Main HTTP client implementation
export class HttpClient implements IHttpClient {
    private readonly config: ApiConfig;
    private readonly requestInterceptors: RequestInterceptor[] = [];
    private readonly responseInterceptors: ResponseInterceptor[] = [];
    private readonly errorInterceptors: ErrorInterceptor[] = [];

    constructor(config: Partial<ApiConfig> = {}) {
        this.config = { ...defaultApiConfig, ...config };
    }

    // Interceptor management
    addRequestInterceptor(interceptor: RequestInterceptor): void {
        this.requestInterceptors.push(interceptor);
    }

    addResponseInterceptor(interceptor: ResponseInterceptor): void {
        this.responseInterceptors.push(interceptor);
    }

    addErrorInterceptor(interceptor: ErrorInterceptor): void {
        this.errorInterceptors.push(interceptor);
    }

    // Public HTTP methods
    async get<T = unknown>(
        endpoint: string,
        params?: Record<string, unknown>,
        options?: RequestOptions
    ): Promise<ApiResponse<T>> {
        const url = this.buildUrl(endpoint, params);
        return this.makeRequest<T>('GET', url.toString(), undefined, options);
    }

    async post<T = unknown>(
        endpoint: string,
        body?: RequestBody,
        options?: RequestOptions
    ): Promise<ApiResponse<T>> {
        return this.makeRequest<T>('POST', endpoint, body, options);
    }

    async put<T = unknown>(
        endpoint: string,
        body?: RequestBody,
        options?: RequestOptions
    ): Promise<ApiResponse<T>> {
        return this.makeRequest<T>('PUT', endpoint, body, options);
    }

    async patch<T = unknown>(
        endpoint: string,
        body?: RequestBody,
        options?: RequestOptions
    ): Promise<ApiResponse<T>> {
        return this.makeRequest<T>('PATCH', endpoint, body, options);
    }

    async delete<T = unknown>(
        endpoint: string,
        options?: RequestOptions
    ): Promise<ApiResponse<T>> {
        return this.makeRequest<T>('DELETE', endpoint, undefined, options);
    }

    // Core request method
    private async makeRequest<T>(
        method: HttpMethod,
        endpoint: string,
        body?: RequestBody,
        options: RequestOptions = {}
    ): Promise<ApiResponse<T>> {
        const {
            timeout = this.config.timeout,
            retries = true,
            skipRetryOn,
            validateResponse = true,
            headers: customHeaders,
            ...fetchOptions
        } = options;

        // Build initial request configuration
        let requestConfig: RequestConfig = {
            method,
            url: this.buildUrl(endpoint).toString(),
            headers: await this.buildHeaders(method, customHeaders),
            body,
            timeout,
            retries,
            skipRetryOn,
            requestId: this.generateRequestId()
        };

        // Apply request interceptors
        for (const interceptor of this.requestInterceptors) {
            requestConfig = await interceptor(requestConfig);
        }

        // Execute request with retry logic
        let lastError: ApiError | null = null;
        const maxAttempts = retries ? this.config.retries.maxAttempts : 1;

        for (let attempt = 1; attempt <= maxAttempts; attempt++) {
            try {
                const response = await this.executeRequest(requestConfig, fetchOptions);

                if (validateResponse && !response.ok) {
                    const error = await this.createErrorFromResponse(response, requestConfig.requestId);

                    if (retries && this.shouldRetry(error, attempt, skipRetryOn)) {
                        const delay = this.calculateBackoffDelay(attempt, response.headers.get('retry-after'));
                        await this.sleep(delay);
                        continue;
                    }

                    // Apply error interceptors
                    for (const interceptor of this.errorInterceptors) {
                        await interceptor(error);
                    }

                    throw error;
                }

                const data = await this.parseResponse<T>(response);
                let apiResponse: ApiResponse<T> = {
                    data,
                    status: response.status,
                    statusText: response.statusText,
                    headers: response.headers,
                    requestId: requestConfig.requestId
                };

                // Apply response interceptors
                for (const interceptor of this.responseInterceptors) {
                    apiResponse = await interceptor(apiResponse) as ApiResponse<T>;
                }

                return apiResponse;
            } catch (error) {
                const apiError = error instanceof ApiError 
                    ? error 
                    : new ApiError(0, 'Unknown error', ApiErrorCode.NETWORK_ERROR, 
                        error instanceof Error ? error.message : 'Unknown', undefined, requestConfig.requestId);
                
                lastError = apiError;

                if (retries && attempt < maxAttempts && this.shouldRetry(apiError, attempt, skipRetryOn)) {
                    const delay = this.calculateBackoffDelay(attempt);
                    await this.sleep(delay);
                    continue;
                }

                // Apply error interceptors
                for (const interceptor of this.errorInterceptors) {
                    await interceptor(apiError);
                }

                throw apiError;
            }
        }

        throw lastError ?? new ApiError(0, 'Request failed', ApiErrorCode.NETWORK_ERROR, 'Request failed after all retry attempts');
    }

    // Execute HTTP request with timeout
    private async executeRequest(config: RequestConfig, fetchOptions: RequestInit): Promise<Response> {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), config.timeout);

        try {
            const requestOptions: RequestInit = {
                method: config.method,
                headers: config.headers,
                signal: controller.signal,
                ...fetchOptions
            };

            // Add body for non-GET requests
            if (config.body !== undefined && config.method !== 'GET') {
                if (config.body instanceof FormData || config.body instanceof Blob || config.body instanceof ArrayBuffer) {
                    requestOptions.body = config.body;
                    // Remove Content-Type for FormData to let browser set boundary
                    if (config.body instanceof FormData) {
                        config.headers.delete('Content-Type');
                    }
                } else if (typeof config.body === 'string') {
                    requestOptions.body = config.body;
                } else {
                    requestOptions.body = JSON.stringify(config.body);
                }
            }

            const response = await fetch(config.url, requestOptions);
            return response;
        } catch (error) {
            if (error instanceof Error && error.name === 'AbortError') {
                throw new ApiError(0, 'Request timeout', ApiErrorCode.TIMEOUT_ERROR, 'Request timed out');
            }
            throw new ApiError(0, 'Network error', ApiErrorCode.NETWORK_ERROR, 
                error instanceof Error ? error.message : 'Unknown network error');
        } finally {
            clearTimeout(timeoutId);
        }
    }

    // Build URL with parameters
    private buildUrl(endpoint: string, params?: Record<string, unknown>): URL {
        const isAbsoluteUrl = /^https?:\/\//i.test(endpoint);
        
        if (isAbsoluteUrl) {
            const url = new URL(endpoint);
            if (params) this.appendParams(url, params);
            return url;
        }

        const baseUrl = this.config.baseUrl.endsWith('/') 
            ? this.config.baseUrl.slice(0, -1) 
            : this.config.baseUrl;
        const cleanEndpoint = endpoint.startsWith('/') ? endpoint : `/${endpoint}`;
        
        const url = new URL(`${baseUrl}${cleanEndpoint}`);
        if (params) this.appendParams(url, params);
        return url;
    }

    // Append parameters to URL
    private appendParams(url: URL, params: Record<string, unknown>): void {
        for (const [key, value] of Object.entries(params)) {
            if (value == null) continue;
            
            if (Array.isArray(value)) {
                value.forEach(item => url.searchParams.append(key, this.paramToString(item)));
            } else {
                url.searchParams.append(key, this.paramToString(value));
            }
        }
    }

    // Convert parameter to string
    private paramToString(value: unknown): string {
        if (value instanceof Date) return value.toISOString();
        return String(value);
    }

    // Build request headers
    private async buildHeaders(method: HttpMethod, customHeaders?: HeadersInit): Promise<Headers> {
        const headers = new Headers(this.config.headers);

        // Add custom headers
        if (customHeaders) {
            if (customHeaders instanceof Headers) {
                customHeaders.forEach((value, key) => headers.set(key, value));
            } else if (Array.isArray(customHeaders)) {
                customHeaders.forEach(([key, value]) => {
                    if (typeof key === 'string' && typeof value === 'string') {
                        headers.set(key, value);
                    }
                });
            } else {
                Object.entries(customHeaders).forEach(([key, value]) => {
                    if (typeof value === 'string') {
                        headers.set(key, value);
                    }
                });
            }
        }

        // Add authentication token
        if (this.config.getAuthToken) {
            try {
                const token = await this.config.getAuthToken();
                if (token && typeof token === 'string') {
                    headers.set('Authorization', `Bearer ${token}`);
                }
            } catch (error) {
                console.warn('Failed to get auth token:', error);
            }
        }

        return headers;
    }

    // Generate unique request ID
    private generateRequestId(): string {
        try {
            if (typeof crypto !== 'undefined' && crypto.randomUUID) {
                return crypto.randomUUID();
            }
        } catch {
            // Fallback for older browsers
        }
        
        return `req-${Date.now().toString(36)}-${Math.random().toString(36).substr(2, 9)}`;
    }

    // Parse response based on content type
    private async parseResponse<T>(response: Response): Promise<T> {
        // Handle 204 No Content and other responses with no body
        if (response.status === 204 || response.headers.get('content-length') === '0') {
            return undefined as T;
        }

        const contentType = response.headers.get('content-type') || '';

        try {
            if (contentType.includes('application/json')) {
                // Check if response body is empty before parsing JSON
                const text = await response.text();
                if (!text || text.trim() === '') {
                    return undefined as T;
                }
                return JSON.parse(text);
            }

            if (contentType.includes('text/')) {
                return (await response.text()) as T;
            }

            if (contentType.includes('application/octet-stream') ||
                contentType.includes('application/pdf') ||
                contentType.includes('application/zip')) {
                return (await response.arrayBuffer()) as T;
            }

            // Default to text for unknown content types
            return (await response.text()) as T;
        } catch (error) {
            throw new ApiError(
                response.status,
                'Parse error',
                ApiErrorCode.VALIDATION_ERROR,
                error instanceof Error ? error.message : 'Failed to parse response'
            );
        }
    }

    // Create error from response
    private async createErrorFromResponse(response: Response, requestId?: string): Promise<ApiError> {
        let message: string;
        let code: ApiErrorCode;

        // Determine error code based on status
        switch (response.status) {
            case 401:
                code = ApiErrorCode.AUTHENTICATION_ERROR;
                message = 'Authentication required';
                break;
            case 403:
                code = ApiErrorCode.AUTHORIZATION_ERROR;
                message = '';
                break;
            case 404:
                code = ApiErrorCode.NOT_FOUND_ERROR;
                message = 'Resource not found';
                break;
            case 429:
                code = ApiErrorCode.RATE_LIMIT_ERROR;
                message = 'Rate limit exceeded';
                break;
            default:
                code = response.status >= 500 ? ApiErrorCode.SERVER_ERROR : ApiErrorCode.VALIDATION_ERROR;
                message = response.statusText || 'Request failed';
        }

        // Try to get detailed error message from response body.
        // Use clone() to avoid consuming the original response stream.
        let errorCode: string | undefined;
        try {
            const cloned = response.clone();
            const contentType = cloned.headers.get('content-type') || '';

            if (contentType.includes('application/json')) {
                const errorData = await cloned.json() as Record<string, unknown>;
                const extracted = this.extractErrorInfo(errorData);
                message = extracted.message || message;
                errorCode = extracted.errorCode;
            } else {
                // Try to parse as JSON even if content-type is missing/wrong
                const textBody = await cloned.text();
                if (textBody) {
                    try {
                        const parsed = JSON.parse(textBody) as Record<string, unknown>;
                        const extracted = this.extractErrorInfo(parsed);
                        message = extracted.message || message;
                        errorCode = extracted.errorCode;
                    } catch {
                        // not JSON — keep original message
                    }
                }
            }
        } catch {
            // Ignore body parsing errors - keep default message
        }

        return new ApiError(response.status, response.statusText, code, message, response, requestId, errorCode);
    }

    // Extract error message and error code from various response formats
    private extractErrorInfo(data: Record<string, unknown>): { message: string | null; errorCode: string | undefined } {
        let message: string | null = null;
        let errorCode: string | undefined;

        // Direct fields
        if (typeof data.message === 'string' && data.message.length > 0) {
            message = data.message;
        }
        if (typeof data.error === 'string' && data.error.length > 0) {
            errorCode = data.error;
            if (!message) {
                message = data.error;
            }
        }

        // Handle detail field - can be string or object
        if (data.detail) {
            if (typeof data.detail === 'string' && data.detail.length > 0) {
                message = message || data.detail;
            } else if (typeof data.detail === 'object' && data.detail !== null) {
                // Nested detail object (e.g., { detail: { message: "...", error: "..." } })
                const detail = data.detail as Record<string, unknown>;
                if (typeof detail.message === 'string' && detail.message.length > 0) {
                    message = message || detail.message;
                }
                if (typeof detail.error === 'string' && detail.error.length > 0) {
                    errorCode = errorCode || detail.error;
                    if (!message) {
                        message = detail.error;
                    }
                }
            }
        }

        return { message, errorCode };
    }

    // Check if request should be retried
    private shouldRetry(error: ApiError, attempt: number, skipRetryOn?: readonly number[]): boolean {
        if (attempt >= this.config.retries.maxAttempts) return false;
        if (skipRetryOn?.includes(error.status)) return false;
        return error.isRetryable();
    }

    // Calculate backoff delay with jitter
    private calculateBackoffDelay(attempt: number, retryAfter?: string | null): number {
        if (retryAfter) {
            const retryAfterMs = /^\d+$/.test(retryAfter) 
                ? parseInt(retryAfter, 10) * 1000 
                : Math.max(0, Date.parse(retryAfter) - Date.now());
            if (retryAfterMs > 0) return retryAfterMs;
        }

        const { baseDelay, backoffFactor, maxDelay } = this.config.retries;
        const delay = baseDelay * Math.pow(backoffFactor, attempt - 1);
        const jitter = Math.random() * 0.1 * delay;
        return Math.min(delay + jitter, maxDelay);
    }

    // Sleep utility
    private sleep(ms: number): Promise<void> {
        return new Promise(resolve => setTimeout(resolve, ms));
    }
}